import { graphHooks } from '@tunasong/graph-lib/react'
import type { Entity, Persisted } from '@tunasong/schemas'
import { useRecentSplit } from '@tunasong/search'
import { Button, EntityGrid, Typography, useFavorites, useNavigateToEntity } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useRef } from 'react'
import { useIsSoloUser } from './use-is-solo-user.js'
import { useSectionPinShortcut } from './use-section-pin-shortcut.js'
import styles from './continue-working-section.module.css'

export interface ContinueWorkingSectionProps {
  /** Empty-state CTA — open the Spaces view. */
  onOpenSpaces(): void
  /** Empty-state CTA — start a new song. */
  onNewSong(): void
}

/**
 * Dashboard Phase 2 — entities I last-edited, sorted by recency.
 *
 * Source: `useRecentSplit().mine` (server-side recent feed, client-side filter on
 * `lastEditedBy === me.userId`). Pinned entities are filtered out so an item appears
 * in exactly one section.
 *
 * The empty state is loud-on-purpose: a brand-new user sees a clear next action.
 */
export const ContinueWorkingSection: FC<ContinueWorkingSectionProps> = props => {
  const { onOpenSpaces, onNewSong } = props
  const { mine, isLoading: isLoadingSplit } = useRecentSplit()
  const { isPinned } = useFavorites()
  const navigateToEntity = useNavigateToEntity()
  const isSolo = useIsSoloUser()
  const sectionRef = useRef<HTMLElement>(null)

  const mineIds = mine.map(e => e.id)
  const { existingEntities = [], isLoading: isResolving } = graphHooks.useEntitiesById(mineIds)
  const entitiesById = new Map(existingEntities.map(e => [e.id, e]))

  const items: Persisted<Entity>[] = mineIds
    .map(id => entitiesById.get(id))
    .filter((e): e is Persisted<Entity> => Boolean(e) && !e?.trash)
    .filter(e => !isPinned(e.id))

  const loading = isLoadingSplit || isResolving

  useSectionPinShortcut({ sectionRef, entities: items })

  if (!loading && items.length === 0) {
    return (
      <section ref={sectionRef} className={styles.section} aria-labelledby="dash-continue-working">
        <Typography id="dash-continue-working" className={styles.heading} variant="h2">
          Continue working
        </Typography>
        <Typography className={styles.emptyHeading} variant="subtitle1">
          Nothing in progress yet.
        </Typography>
        <Typography className={styles.emptyBody} variant="caption">
          Open a Space or create a song to start.
        </Typography>
        <div className={styles.emptyActions}>
          <Button onClick={onOpenSpaces}>Open a Space</Button>
          <Button onClick={onNewSong}>New Song</Button>
        </div>
      </section>
    )
  }

  return (
    <section ref={sectionRef} className={styles.section} aria-labelledby="dash-continue-working">
      <Typography id="dash-continue-working" className={styles.heading} variant="h2">
        Continue working
      </Typography>
      <Typography className={styles.caption} variant="caption">
        Pick up where you left off.
      </Typography>
      <EntityGrid size="medium" entities={items} onOpen={navigateToEntity} showLastEditor={!isSolo} showParentSpace />
    </section>
  )
}

export default ContinueWorkingSection
